import React, { useState, useEffect, useCallback, useRef } from 'react';
import { request } from '../../services/apiClient';

/**
 * FloatingPolishButton - 텍스트 선택 시 나타나는 AI 정리 버튼
 *
 * 사용자가 읽기 모드에서 텍스트를 드래그하면 선택 영역 위에 버튼이 표시됨.
 * 클릭하면 GPT Mini가 전체 문서 맥락을 고려하여 선택 영역을 정리함.
 */
export default function FloatingPolishButton({ fullContent, onPolished }) {
  const [selection, setSelection] = useState(null);
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diff, setDiff] = useState(null); // { original, polished }
  const [error, setError] = useState(null);
  const btnRef = useRef(null);

  // Listen for text selection changes
  const handleSelectionChange = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) {
      // Don't clear immediately — let user click the button
      return;
    }

    const text = sel.toString().trim();
    if (text.length < 2) return;

    // Check if selection is within editor content area
    const range = sel.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const editorContent = container.nodeType === 1
      ? container.closest('.bear-editor-content')
      : container.parentElement?.closest('.bear-editor-content');

    if (!editorContent) return;

    // Get position for the floating button
    const rect = range.getBoundingClientRect();
    setPosition({
      top: rect.top - 40,
      left: rect.left + rect.width / 2,
    });
    setSelection(text);
    setDiff(null);
    setError(null);
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [handleSelectionChange]);

  // Clear on click outside
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (diff) return; // Don't clear while showing diff
      // Delay clearing to allow selection to complete
      setTimeout(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) {
          setSelection(null);
          setPosition(null);
        }
      }, 200);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [diff]);

  const handlePolish = async () => {
    if (!selection || !fullContent) return;
    setLoading(true);
    setError(null);

    try {
      const result = await request('/ai/polish', {
        method: 'POST',
        body: JSON.stringify({
          fullContent,
          selectedText: selection,
        }),
      });

      if (result.success && result.polishedText) {
        if (result.polishedText === selection) {
          setDiff(null);
          setError('변경할 내용이 없습니다');
          setTimeout(() => { setError(null); setSelection(null); setPosition(null); }, 2000);
        } else {
          setDiff({ original: selection, polished: result.polishedText });
        }
      } else {
        setError(result.error || 'AI 정리 실패');
      }
    } catch (err) {
      setError(err.message || 'AI 정리 중 오류');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (diff && onPolished) {
      onPolished(diff.original, diff.polished);
    }
    setDiff(null);
    setSelection(null);
    setPosition(null);
  };

  const handleReject = () => {
    setDiff(null);
    setSelection(null);
    setPosition(null);
  };

  if (!selection || !position) return null;

  return (
    <>
      {/* Floating button */}
      {!diff && (
        <div
          ref={btnRef}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translateX(-50%)',
            zIndex: 10000,
          }}
        >
          <button
            onClick={handlePolish}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 12px',
              background: loading ? '#94a3b8' : '#3B82F6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: loading ? 'wait' : 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              whiteSpace: 'nowrap',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { if (!loading) e.target.style.background = '#2563EB'; }}
            onMouseLeave={(e) => { if (!loading) e.target.style.background = '#3B82F6'; }}
          >
            {loading ? (
              <>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                정리 중...
              </>
            ) : (
              <>✨ AI 정리</>
            )}
          </button>
          {error && (
            <div style={{
              marginTop: '4px',
              padding: '4px 8px',
              background: '#fef2f2',
              color: '#dc2626',
              borderRadius: '4px',
              fontSize: '11px',
              textAlign: 'center',
            }}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* Diff modal */}
      {diff && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.3)',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '24px',
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>
              AI 정리 결과
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626', marginBottom: '8px' }}>
                  원본
                </div>
                <pre style={{
                  background: '#fef2f2',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: '1px solid #fecaca',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}>
                  {diff.original}
                </pre>
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#16a34a', marginBottom: '8px' }}>
                  정리됨
                </div>
                <pre style={{
                  background: '#f0fdf4',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: '1px solid #bbf7d0',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}>
                  {diff.polished}
                </pre>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={handleReject}
                style={{
                  padding: '8px 16px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleAccept}
                style={{
                  padding: '8px 16px',
                  background: '#3B82F6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                적용
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
